/**
 * SlashMenu — trigger `/`, listă unificată de comenzi:
 * - formatare (H1/H2/H3, listă, code, quote)
 * - credentials tenant (filtrate după query)
 * - placeholder imagine (va fi implementat în faza image upload)
 *
 * Filtrarea: comparăm query-ul cu labelul fiecărui item case-insensitive.
 * Ex. `/hea` → H1/H2/H3, `/gith` → credențialul GitHub.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Extension, type Editor, type Range } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import 'tippy.js/dist/tippy.css';

const SLASH_MENU_KEY = new PluginKey('cerbes-slash-menu');

import { makeSuggestionRenderer } from '@/features/notes/suggestionRenderer';
import {
  AlertIcon,
  BoldIcon,
  CheckSquareIcon,
  ChevronRightIcon,
  CodeIcon,
  H1Icon,
  H2Icon,
  H3Icon,
  ImageIcon,
  InfoIcon,
  KeyIcon,
  ListIcon,
  MinusIcon,
  OrderedListIcon,
  PaperclipIcon,
  QuoteIcon,
  TableIcon,
} from '@/features/notes/icons';
import i18n from '@/i18n';

export type UploadKind = 'image' | 'file';

export interface SlashItem {
  key: string;
  label: string;
  hint?: string;
  disabledHint?: string;
  icon: ReactNode;
  keywords: string[];
  action: (editor: Editor, range: { from: number; to: number }) => void;
  disabled?: boolean;
}

function staticItems(openUploadModal?: (kind: UploadKind) => void): SlashItem[] {
  return [
    {
      key: 'h1',
      label: i18n.t('notes_blocks.slash.heading1'),
      hint: i18n.t('notes_blocks.slash.heading1_hint'),
      icon: <H1Icon />,
      keywords: ['h1', 'heading', 'titlu'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleHeading({ level: 1 }).run(),
    },
    {
      key: 'h2',
      label: i18n.t('notes_blocks.slash.heading2'),
      icon: <H2Icon />,
      keywords: ['h2', 'heading', 'subtitlu'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleHeading({ level: 2 }).run(),
    },
    {
      key: 'h3',
      label: i18n.t('notes_blocks.slash.heading3'),
      icon: <H3Icon />,
      keywords: ['h3', 'heading'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleHeading({ level: 3 }).run(),
    },
    {
      key: 'bullet',
      label: i18n.t('notes_blocks.slash.bullet_list'),
      icon: <ListIcon />,
      keywords: ['list', 'bullet', 'lista'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleBulletList().run(),
    },
    {
      key: 'ordered',
      label: i18n.t('notes_blocks.slash.ordered_list'),
      icon: <OrderedListIcon />,
      keywords: ['list', 'ordered', 'numar'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
    },
    {
      key: 'code',
      label: i18n.t('notes_blocks.slash.code_block'),
      icon: <CodeIcon />,
      keywords: ['code', 'cod'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
    },
    {
      key: 'quote',
      label: i18n.t('notes_blocks.slash.blockquote'),
      icon: <QuoteIcon />,
      keywords: ['quote', 'citat'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
    },
    {
      key: 'bold',
      label: i18n.t('notes_blocks.slash.bold'),
      icon: <BoldIcon />,
      keywords: ['bold'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleBold().run(),
    },
    {
      key: 'todo',
      label: i18n.t('notes_blocks.slash.task_list'),
      hint: i18n.t('notes_blocks.slash.task_list_hint'),
      icon: <CheckSquareIcon />,
      keywords: ['todo', 'task', 'checkbox', 'check'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleTaskList().run(),
    },
    {
      key: 'divider',
      label: i18n.t('notes_blocks.slash.divider'),
      hint: i18n.t('notes_blocks.slash.divider_hint'),
      icon: <MinusIcon />,
      keywords: ['divider', 'hr', 'separator', 'linie'],
      action: (editor, range) =>
        editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
    },
    {
      key: 'toggle',
      label: i18n.t('notes_blocks.slash.toggle'),
      hint: i18n.t('notes_blocks.slash.toggle_hint'),
      icon: <ChevronRightIcon />,
      keywords: ['toggle', 'collapse', 'details', 'sumar'],
      action: (editor, range) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'toggleBlock',
            attrs: { summary: i18n.t('notes_blocks.toggle.default_summary') },
            content: [{ type: 'paragraph' }],
          })
          .run(),
    },
    {
      key: 'notice',
      label: i18n.t('notes_blocks.slash.notice'),
      hint: i18n.t('notes_blocks.slash.notice_hint'),
      icon: <InfoIcon />,
      keywords: ['notice', 'info', 'success', 'warning', 'danger', 'callout', 'box'],
      action: (editor, range) =>
        insertNotice(editor, range, 'info'),
    },
    {
      key: 'secret',
      label: i18n.t('notes_blocks.slash.secret'),
      hint: i18n.t('notes_blocks.slash.secret_hint'),
      icon: <KeyIcon />,
      keywords: ['secret', 'password', 'parola', 'mask', 'ascuns', 'eye', 'pwd'],
      action: (editor, range) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertContent({
            type: 'secretInline',
            attrs: { value: '' },
          })
          .run(),
    },
    {
      key: 'table',
      label: i18n.t('notes_blocks.slash.table'),
      hint: i18n.t('notes_blocks.slash.table_hint'),
      icon: <TableIcon />,
      keywords: ['table', 'tabel', 'grid'],
      action: (editor, range) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
          .run(),
    },
    {
      key: 'image',
      label: i18n.t('notes_blocks.slash.image'),
      hint: i18n.t('notes_blocks.slash.image_hint'),
      disabledHint: i18n.t('notes_blocks.slash.image_disabled_hint'),
      icon: <ImageIcon />,
      keywords: ['image', 'imagine', 'photo', 'poza'],
      disabled: !openUploadModal,
      action: (editor, range) => {
        editor.chain().focus().deleteRange(range).run();
        if (openUploadModal) openUploadModal('image');
      },
    },
    {
      key: 'file',
      label: i18n.t('notes_blocks.slash.file'),
      hint: i18n.t('notes_blocks.slash.file_hint'),
      disabledHint: i18n.t('notes_blocks.slash.file_disabled_hint'),
      icon: <PaperclipIcon />,
      keywords: ['file', 'fisier', 'atasament', 'attachment', 'pdf', 'doc'],
      disabled: !openUploadModal,
      action: (editor, range) => {
        editor.chain().focus().deleteRange(range).run();
        if (openUploadModal) openUploadModal('file');
      },
    },
  ];
}

function insertNotice(
  editor: Editor,
  range: { from: number; to: number },
  variant: 'info' | 'success' | 'warning' | 'danger',
): void {
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent({
      type: 'noticeBlock',
      attrs: { variant },
      content: [{ type: 'paragraph' }],
    })
    .run();
}

function filterItems(items: SlashItem[], query: string): SlashItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((it) => {
    if (it.label.toLowerCase().includes(q)) return true;
    return it.keywords.some((k) => k.toLowerCase().includes(q));
  });
}

// ==========================================================
// Extension factory
// ==========================================================

export function createSlashMenu(
  openUploadModal?: (kind: UploadKind) => void,
): Extension {
  return Extension.create({
    name: 'slashMenu',
    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          pluginKey: SLASH_MENU_KEY,
          char: '/',
          startOfLine: false,
          allowSpaces: false,
          items: ({ query }: { query: string }) => {
            return filterItems(staticItems(openUploadModal), query);
          },
          command: ({
            editor,
            range,
            props: item,
          }: {
            editor: Editor;
            range: Range;
            props: SlashItem;
          }) => {
            if (item.disabled) return;
            item.action(editor, range);
          },
          render: makeSuggestionRenderer(SlashList, (item) => !item.disabled),
        } as SuggestionOptions),
      ];
    },
  });
}

// ==========================================================
// Tippy renderer
// ==========================================================

// ==========================================================
// Listă popover
// ==========================================================

function SlashList(props: {
  items: SlashItem[];
  command: (item: SlashItem) => void;
  selectedIndex: number;
}) {
  const { items, command, selectedIndex } = props;
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (items.length === 0) {
    return (
      <div className="bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-md shadow-lg px-3 py-2 text-xs text-[var(--text-muted)]">
        {t('notes_blocks.slash.empty')}
      </div>
    );
  }
  return (
    <div
      ref={containerRef}
      className="bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-md shadow-lg py-1 max-h-80 overflow-auto min-w-[280px]"
    >
      {items.map((item, idx) => {
        const active = idx === selectedIndex;
        const isDisabled = !!item.disabled;
        const shownHint = isDisabled && item.disabledHint ? item.disabledHint : item.hint;
        return (
          <button
            key={item.key}
            ref={active ? activeRef : undefined}
            type="button"
            onClick={() => !isDisabled && command(item)}
            disabled={isDisabled}
            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
              isDisabled
                ? 'cursor-not-allowed text-[var(--text-dim)]'
                : active
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                  : 'text-[var(--text)] hover:bg-[var(--bg-hover)]'
            }`}
          >
            <span
              className={`flex items-center justify-center w-6 h-6 rounded flex-shrink-0 ${
                isDisabled
                  ? 'text-[var(--text-dim)]'
                  : active
                    ? 'text-brand-700 dark:text-brand-300'
                    : 'text-[var(--text-muted)]'
              }`}
            >
              {item.icon}
            </span>
            <span className="flex-1 min-w-0">
              <div className={`font-medium truncate ${isDisabled ? 'line-through decoration-slate-300 dark:decoration-slate-600' : ''}`}>
                {item.label}
              </div>
              {shownHint && (
                <div
                  className={`text-[10px] truncate flex items-center gap-1 ${
                    isDisabled
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-[var(--text-muted)]'
                  }`}
                >
                  {isDisabled && <AlertIcon className="w-3 h-3 flex-shrink-0" />}
                  <span className="truncate">{shownHint}</span>
                </div>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
